import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, TrendingUp, Phone, CheckCircle, Clock, Target, Award } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { TelecallerService } from '../../services/telecallerService';

const TelecallerPerformance = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedPeriod, setSelectedPeriod] = useState('week');

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['telecallerPerformance', user?.id, selectedPeriod],
    queryFn: () => TelecallerService.getTelecallerStats(user?.id),
    enabled: !!user?.id,
  });

  const stats = statsData?.data || {};

  const performanceMetrics = [
    {
      label: 'Total Leads',
      value: stats.assignedLeads || 0,
      icon: Phone,
      color: 'bg-blue-100 text-blue-700',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Called Today',
      value: stats.calledToday || 0,
      icon: Clock,
      color: 'bg-purple-100 text-purple-700',
      iconColor: 'text-purple-600',
    },
    {
      label: 'Confirmed',
      value: stats.confirmed || 0,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-700',
      iconColor: 'text-green-600',
    },
    {
      label: 'Booked',
      value: stats.booked || 0,
      icon: Award,
      color: 'bg-yellow-100 text-yellow-700',
      iconColor: 'text-yellow-600',
    },
    {
      label: 'Conversion Rate',
      value: `${stats.conversionRate || 0}%`,
      icon: TrendingUp,
      color: 'bg-indigo-100 text-indigo-700',
      iconColor: 'text-indigo-600',
    },
    {
      label: 'Target Achievement',
      value: `${stats.targetAchievement || 0}%`,
      icon: Target,
      color: 'bg-red-100 text-red-700',
      iconColor: 'text-red-600',
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/telecaller/more')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Performance</h1>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/telecaller/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Performance</h1>
          <p className="text-gray-600 mt-1">Track your sales performance metrics</p>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-2">
          {['day', 'week', 'month', 'year'].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                selectedPeriod === period
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {performanceMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${metric.color}`}>
                  <Icon size={24} className={metric.iconColor} />
                </div>
                <BarChart3 size={20} className="text-gray-400" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</p>
              <p className="text-sm text-gray-600">{metric.label}</p>
            </div>
          );
        })}
      </div>

      {/* Performance Chart Placeholder */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Trend</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <p className="text-gray-500">Performance chart visualization coming soon</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Lead Confirmed</p>
              <p className="text-sm text-gray-500">2 hours ago</p>
            </div>
            <CheckCircle size={20} className="text-green-600" />
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Order Booked</p>
              <p className="text-sm text-gray-500">5 hours ago</p>
            </div>
            <Award size={20} className="text-yellow-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelecallerPerformance;

